import { addYears, format, fromUnixTime } from "date-fns"

function formatTimestampToSchemaDate(timestamp?: number) {
  if (!timestamp)
    return undefined
  return format(fromUnixTime(timestamp), "yyyy-MM-dd")
}

function getValidThroughDate(timestamp?: number) {
  if (!timestamp)
    return undefined
  const createDate = fromUnixTime(timestamp)
  const validThroughDate = addYears(createDate, 10)
  return format(validThroughDate, "yyyy-MM-dd")
}

function getEmploymentType(workingTimeTypes?: string[]) {
  if (!workingTimeTypes?.length)
    return undefined

  const workingTimeMap: Record<string, string> = {
    FULL_TIME: "FULL_TIME",
    PART_TIME: "PART_TIME",
    FLEXIBLE: "OTHER",
  }

  const mappedTypes = workingTimeTypes
    .map(type => workingTimeMap[type] || "OTHER") as Array<"FULL_TIME" | "PART_TIME" | "OTHER">

  return mappedTypes.length === 1 ? mappedTypes[0] : mappedTypes
}

function getJobLocation(locationObjects?: JobLocationObject[]) {
  if (!locationObjects?.length)
    return undefined

  const locations = locationObjects.map((loc) => {
    return definePlace({
      name: loc.name,
      address: {
        streetAddress: loc.street || undefined,
        addressLocality: loc.city || undefined,
        addressRegion: loc.state || undefined,
        postalCode: loc.zip || undefined,
        addressCountry: loc.country || undefined,
      },
      geo: loc.lat && loc.lng
        ? {
            latitude: loc.lat,
            longitude: loc.lng,
          }
        : undefined,
    })
  })

  return locations.length === 1 ? locations[0] : locations
}

function getApplicantLocationRequirements(locationObjects?: JobLocationObject[]) {
  if (!locationObjects?.length)
    return undefined

  const countries = locationObjects
    .map(loc => loc.country)
    .filter(Boolean)
    .filter((country, index, self) => self.indexOf(country) === index) // Remove duplicates

  if (!countries.length)
    return undefined

  const countryObjects = countries.map(country => ({
    "@type": "Country" as const,
    "name": country,
  }))

  return countryObjects.length === 1 ? countryObjects[0] : countryObjects
}

function getApplicationContact(contactData?: FlatJob["contact_data"]) {
  if (!contactData)
    return undefined

  const name = [contactData.first_name, contactData.last_name]
    .filter(Boolean)
    .join(" ")

  // Only return contact if at least one field has a value
  if (!name && !contactData.email && !contactData.phone)
    return undefined

  return {
    "@type": "ContactPoint" as const,
    "name": name || undefined,
    "email": contactData.email || undefined,
    "telephone": contactData.phone || undefined,
  }
}

function getEducationRequirements(careerLevel?: string[]) {
  if (!careerLevel?.length)
    return undefined

  return careerLevel.join(", ")
}

/**
 * Generates JobPosting schema.org structured data for a job detail page.
 * Uses the provided job data, maps them to the appropriate schema fields,
 * and transforms them if necessary (e.g., dates, locations, employment type).
 * Automatically registers the schema with nuxt-schema-org for SEO optimization.
 *
 * @param job - The flat job object containing all job details
 * @param heroImage - Optional hero image URL to include in the schema
 */
export function useJobPostingSchema(job: FlatJob, heroImage?: string | null) {
  const hiringOrganization = defineOrganization({
    name: job.company,
    logo: job.company_logo || undefined,
    url: job.company_url || "/",
  })

  const employmentUnit = job.department?.length
    ? defineOrganization({
      name: job.department.join(", "),
      url: "/",
    })
    : undefined

  const image = heroImage
    ? defineImage({
      "@id": `${job.url}#hero-image`,
      "url": heroImage,
    })
    : undefined

  const isRemote = job.schema_values?.location_types?.includes("REMOTE") ?? false

  useSchemaOrg([
    defineJobPosting({
      "@id": `${job.url}#job-posting`,
      "title": job.title,
      "description": job.description,
      "datePosted": formatTimestampToSchemaDate(job.status_change_date_timestamp || job.create_date_timestamp),
      "validThrough": getValidThroughDate(job.start_date_timestamp),
      "jobStartDate": formatTimestampToSchemaDate(job.start_date_timestamp),
      "identifier": {
        name: "id",
        value: job.offer_uuid,
      },
      hiringOrganization,
      employmentUnit,
      "jobLocation": isRemote ? undefined : getJobLocation(job.location_objects),
      "jobLocationType": isRemote ? "TELECOMMUTE" : undefined,
      "applicantLocationRequirements": isRemote ? getApplicantLocationRequirements(job.location_objects) : undefined,
      "directApply": job.ats_application_customer_name ? true : undefined,
      "url": job.url,
      image,
      "employmentType": getEmploymentType(job.schema_values?.working_time_types),
      "workHours": job.working_hours?.length ? job.working_hours.join(", ") : undefined,
      "educationRequirements": getEducationRequirements(job.career_level),
      "occupationalCategory": job.department?.length ? job.department.join(", ") : undefined,
      "applicationContact": getApplicationContact(job.contact_data),
      "incentiveCompensation": job.offering || undefined,
      "responsibilities": job.expectation || undefined,
      "additionalInformation": job.additional || undefined,
    }),
  ])
}
